import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  AccountingExport,
  ApprovalDelegation,
  CostCode,
  CostCodeReport,
  CreateApprovalDelegationPayload,
  CreateCostCodePayload,
  CreateExpensePolicyPayload,
  CreatePayPeriodPayload,
  CreateRecurringExpensePayload,
  CreateTechPayRatePayload,
  DashboardStats,
  Expense,
  ExpenseByProjectReport,
  ExpenseByTechReport,
  ExpenseListItem,
  ExpenseCategory,
  ExpensePolicy,
  ExpenseReceipt,
  ExpenseAllocation,
  GenerateInvoiceItemsResponse,
  MileageEntry,
  PaginatedResponse,
  PayPeriod,
  PayrollRun,
  PayrollRunListItem,
  PayrollAdjustment,
  PayrollRunSnapshot,
  PayrollRunWithReadiness,
  PayrollPreview,
  PayrollSettings,
  PayrollSummaryReport,
  PerDiemEntry,
  PerDiemWorker,
  PolicyCheckResult,
  QuickBooksAccountMapping,
  QuickBooksConnectionStatus,
  QuickBooksSyncPayload,
  QuickBooksSyncResult,
  CreateQBAccountMappingPayload,
  UpdateQBAccountMappingPayload,
  ReceiptOCRResult,
  RecurringExpense,
  TimesheetReconciliation,
  CreateExpensePayload,
  UpdateExpensePayload,
  CreateExpenseCategoryPayload,
  CreateExpenseAllocationPayload,
  CreateMileagePayload,
  UpdateMileagePayload,
  CreatePerDiemPayload,
  UpdatePerDiemPayload,
  BulkPerDiemPayload,
  BulkPerDiemResponse,
  BulkApprovePayload,
  BulkApproveResponse,
  UpdatePayrollSettingsPayload,
  UpdateCostCodePayload,
  ExpenseStatus,
  MileageStatus,
  PerDiemStatus,
  TechPayRate,
  Timesheet,
  TimesheetApproveResult,
  TimesheetListItem,
  TimesheetStatus,
  GenerateTimesheetPayload,
  GenerateTimesheetResponse,
  SetManualTimesheetEntriesPayload,
  JobCostSummary,
  JobCostDetail,
} from '@/lib/types';

const PAYROLL_API = '/api/v1/payroll';

// ─── Filter Types ───────────────────────────────────────────────────────────

export interface ExpenseFilters {
  page?: number;
  page_size?: number;
  status?: ExpenseStatus | '';
  person?: number;
  tech?: number;
  project?: number;
  category?: number;
  date_from?: string;
  date_to?: string;
  is_billable?: boolean | '';
}

export interface MileageFilters {
  page?: number;
  page_size?: number;
  status?: MileageStatus | '';
  person?: number;
  tech?: number;
  project?: number;
  date_from?: string;
  date_to?: string;
}

export interface PerDiemFilters {
  page?: number;
  page_size?: number;
  status?: PerDiemStatus | '';
  person?: number;
  tech?: number;
  project?: number;
  date_from?: string;
  date_to?: string;
}

export interface ExpenseCategoryFilters {
  is_active?: boolean;
}

function cleanFilters<T extends object>(filters: T): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value !== undefined)
  );
}

// ─── Payroll Settings Hooks ─────────────────────────────────────────────────

export function usePayrollSettings() {
  return useQuery({
    queryKey: queryKeys.payrollSettings.detail(),
    queryFn: async (): Promise<PayrollSettings> => {
      const response = await apiClient.get<PayrollSettings>(
        `${PAYROLL_API}/settings/`
      );
      return response.data;
    },
  });
}

export function useUpdatePayrollSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: UpdatePayrollSettingsPayload): Promise<PayrollSettings> => {
      const response = await apiClient.patch<PayrollSettings>(
        `${PAYROLL_API}/settings/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollSettings.all });
    },
  });
}

// ─── Expense Category Hooks ─────────────────────────────────────────────────

export function useExpenseCategories(filters: ExpenseCategoryFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.expenseCategories.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ExpenseCategory>> => {
      const response = await apiClient.get<PaginatedResponse<ExpenseCategory>>(
        `${PAYROLL_API}/categories/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateExpenseCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateExpenseCategoryPayload): Promise<ExpenseCategory> => {
      const response = await apiClient.post<ExpenseCategory>(
        `${PAYROLL_API}/categories/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expenseCategories.all });
    },
  });
}

// ─── Expense Hooks ──────────────────────────────────────────────────────────

export function useExpenses(filters: ExpenseFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.expenses.list(clean),
    queryFn: async (): Promise<ExpenseListItem[]> => {
      const response = await apiClient.get<ExpenseListItem[]>(
        `${PAYROLL_API}/expenses/`,
        { params: clean }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
}

export function useExpense(id: number) {
  return useQuery({
    queryKey: queryKeys.expenses.detail(id),
    queryFn: async (): Promise<Expense> => {
      const response = await apiClient.get<Expense>(
        `${PAYROLL_API}/expenses/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useMyExpenses(filters: { status?: ExpenseStatus | '' } = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.expenses.myExpenses(clean),
    queryFn: async (): Promise<ExpenseListItem[]> => {
      const response = await apiClient.get<ExpenseListItem[]>(
        `${PAYROLL_API}/expenses/my-expenses/`,
        { params: clean }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    retry: false,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateExpensePayload): Promise<Expense> => {
      const response = await apiClient.post<Expense>(
        `${PAYROLL_API}/expenses/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateExpensePayload;
    }): Promise<Expense> => {
      const response = await apiClient.patch<Expense>(
        `${PAYROLL_API}/expenses/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(variables.id) });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/expenses/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
  });
}

export function useSubmitExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Expense> => {
      const response = await apiClient.post<Expense>(
        `${PAYROLL_API}/expenses/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(id) });
    },
  });
}

export interface ApproveVariables {
  id: number;
  /** H3PAY-D9: the approver confirmed approving their own record. */
  confirmSelfApproval?: boolean;
}

function approveRequest(variables: number | ApproveVariables) {
  const { id, confirmSelfApproval } =
    typeof variables === 'number' ? { id: variables, confirmSelfApproval: false } : variables;
  return { id, body: confirmSelfApproval ? { confirm_self_approval: true } : undefined };
}

export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (variables: number | ApproveVariables): Promise<Expense> => {
      const { id, body } = approveRequest(variables);
      const response = await apiClient.post<Expense>(
        `${PAYROLL_API}/expenses/${id}/approve/`,
        body
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(approveRequest(variables).id) });
    },
  });
}

export function useRejectExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      reason,
    }: {
      id: number;
      reason: string;
    }): Promise<Expense> => {
      const response = await apiClient.post<Expense>(
        `${PAYROLL_API}/expenses/${id}/reject/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(variables.id) });
    },
  });
}

export function useReimburseExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Expense> => {
      const response = await apiClient.post<Expense>(
        `${PAYROLL_API}/expenses/${id}/reimburse/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(id) });
    },
  });
}

export function useBulkApproveExpenses() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BulkApprovePayload): Promise<BulkApproveResponse> => {
      const response = await apiClient.post<BulkApproveResponse>(
        `${PAYROLL_API}/expenses/bulk-approve/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.all });
    },
  });
}

export function useUploadReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      expenseId,
      file,
    }: {
      expenseId: number;
      file: File;
    }): Promise<ExpenseReceipt> => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<ExpenseReceipt>(
        `${PAYROLL_API}/expenses/${expenseId}/receipts/`,
        formData
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(variables.expenseId) });
    },
  });
}

export function useAddExpenseAllocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      expenseId,
      data,
    }: {
      expenseId: number;
      data: CreateExpenseAllocationPayload;
    }): Promise<ExpenseAllocation> => {
      const response = await apiClient.post<ExpenseAllocation>(
        `${PAYROLL_API}/expenses/${expenseId}/allocations/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.expenses.detail(variables.expenseId) });
    },
  });
}

// ─── Mileage Hooks ──────────────────────────────────────────────────────────

export function useMileageEntries(filters: MileageFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.mileage.list(clean),
    queryFn: async (): Promise<MileageEntry[]> => {
      const response = await apiClient.get<MileageEntry[]>(
        `${PAYROLL_API}/mileage/`,
        { params: clean }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
}

export function useMileageEntry(id: number) {
  return useQuery({
    queryKey: queryKeys.mileage.detail(id),
    queryFn: async (): Promise<MileageEntry> => {
      const response = await apiClient.get<MileageEntry>(
        `${PAYROLL_API}/mileage/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateMileage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateMileagePayload): Promise<MileageEntry> => {
      const response = await apiClient.post<MileageEntry>(
        `${PAYROLL_API}/mileage/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.mileage.all });
    },
  });
}

export function useUpdateMileage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateMileagePayload;
    }): Promise<MileageEntry> => {
      const response = await apiClient.patch<MileageEntry>(
        `${PAYROLL_API}/mileage/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.mileage.all });
      qc.invalidateQueries({ queryKey: queryKeys.mileage.detail(variables.id) });
    },
  });
}

export function useSubmitMileage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<MileageEntry> => {
      const response = await apiClient.post<MileageEntry>(
        `${PAYROLL_API}/mileage/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.mileage.all });
      qc.invalidateQueries({ queryKey: queryKeys.mileage.detail(id) });
    },
  });
}

export function useApproveMileage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<MileageEntry> => {
      const response = await apiClient.post<MileageEntry>(
        `${PAYROLL_API}/mileage/${id}/approve/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.mileage.all });
      qc.invalidateQueries({ queryKey: queryKeys.mileage.detail(id) });
    },
  });
}

export function useRejectMileage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<MileageEntry> => {
      const response = await apiClient.post<MileageEntry>(
        `${PAYROLL_API}/mileage/${id}/reject/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.mileage.all });
      qc.invalidateQueries({ queryKey: queryKeys.mileage.detail(id) });
    },
  });
}

// ─── Per Diem Hooks ─────────────────────────────────────────────────────────

export function usePerDiemWorkers(enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.perDiem.all, 'workers'],
    queryFn: async (): Promise<PerDiemWorker[]> => {
      const response = await apiClient.get<PerDiemWorker[]>(
        `${PAYROLL_API}/per-diem/workers/`
      );
      return response.data;
    },
    enabled,
  });
}

export function usePerDiemEntries(filters: PerDiemFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.perDiem.list(clean),
    queryFn: async (): Promise<PerDiemEntry[]> => {
      const response = await apiClient.get<PerDiemEntry[]>(
        `${PAYROLL_API}/per-diem/`,
        { params: clean }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
}

export function usePerDiemEntry(id: number) {
  return useQuery({
    queryKey: queryKeys.perDiem.detail(id),
    queryFn: async (): Promise<PerDiemEntry> => {
      const response = await apiClient.get<PerDiemEntry>(
        `${PAYROLL_API}/per-diem/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreatePerDiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePerDiemPayload): Promise<PerDiemEntry> => {
      const response = await apiClient.post<PerDiemEntry>(
        `${PAYROLL_API}/per-diem/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.all });
    },
  });
}

export function useUpdatePerDiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePerDiemPayload;
    }): Promise<PerDiemEntry> => {
      const response = await apiClient.patch<PerDiemEntry>(
        `${PAYROLL_API}/per-diem/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.all });
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.detail(variables.id) });
    },
  });
}

export function useSubmitPerDiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PerDiemEntry> => {
      const response = await apiClient.post<PerDiemEntry>(
        `${PAYROLL_API}/per-diem/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.all });
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.detail(id) });
    },
  });
}

export function useApprovePerDiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (variables: number | ApproveVariables): Promise<PerDiemEntry> => {
      const { id, body } = approveRequest(variables);
      const response = await apiClient.post<PerDiemEntry>(
        `${PAYROLL_API}/per-diem/${id}/approve/`,
        body
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.all });
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.detail(approveRequest(variables).id) });
    },
  });
}

export function useBulkCreatePerDiem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: BulkPerDiemPayload): Promise<BulkPerDiemResponse> => {
      const response = await apiClient.post<BulkPerDiemResponse>(
        `${PAYROLL_API}/per-diem/bulk-create/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.perDiem.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Timesheet Hooks
// ---------------------------------------------------------------------------

export interface TimesheetFilters {
  status?: TimesheetStatus;
  tech?: number;
  week?: string;
  date_from?: string;
  date_to?: string;
}

export function useTimesheets(filters: TimesheetFilters = {}) {
  return useQuery({
    queryKey: queryKeys.timesheets.list(cleanFilters(filters)),
    queryFn: async (): Promise<TimesheetListItem[]> => {
      const response = await apiClient.get<TimesheetListItem[]>(
        `${PAYROLL_API}/timesheets/`,
        { params: cleanFilters(filters) }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
  });
}

export function useTimesheet(id: number) {
  return useQuery({
    queryKey: queryKeys.timesheets.detail(id),
    queryFn: async (): Promise<Timesheet> => {
      const response = await apiClient.get<Timesheet>(
        `${PAYROLL_API}/timesheets/${id}/`
      );
      return response.data;
    },
    enabled: !!id,
  });
}

export function useMyTimesheets(
  filters: TimesheetFilters = {},
  { enabled = true } = {}
) {
  return useQuery({
    queryKey: queryKeys.timesheets.myTimesheets(cleanFilters(filters)),
    queryFn: async (): Promise<TimesheetListItem[]> => {
      const response = await apiClient.get<TimesheetListItem[]>(
        `${PAYROLL_API}/timesheets/my-timesheets/`,
        { params: cleanFilters(filters) }
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled,
    retry: false,
  });
}

export function useGenerateTimesheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: GenerateTimesheetPayload): Promise<GenerateTimesheetResponse> => {
      const response = await apiClient.post<GenerateTimesheetResponse>(
        `${PAYROLL_API}/timesheets/generate/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
}

export function useSubmitTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<Timesheet> => {
      const response = await apiClient.post<Timesheet>(
        `${PAYROLL_API}/timesheets/${id}/submit/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
}

export function useApproveTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      notes,
      confirmSelfApproval,
    }: ApproveVariables & { notes?: string }): Promise<TimesheetApproveResult> => {
      const response = await apiClient.post<TimesheetApproveResult>(
        `${PAYROLL_API}/timesheets/${id}/approve/`,
        confirmSelfApproval ? { notes, confirm_self_approval: true } : { notes }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
}

export function useRejectTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }): Promise<Timesheet> => {
      const response = await apiClient.post<Timesheet>(
        `${PAYROLL_API}/timesheets/${id}/reject/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
}

export function useSetManualTimesheetEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, entries }: SetManualTimesheetEntriesPayload): Promise<Timesheet> => {
      const response = await apiClient.post<Timesheet>(
        `${PAYROLL_API}/timesheets/${id}/set-manual-entries/`,
        { entries }
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
    },
  });
}

export interface SetTimesheetDayRateChoicePayload {
  id: number;
  work_date: string;
  choice: 'job' | 'split' | 'custom';
  project?: number;
  custom_rate?: string;
  reason?: string;
  confirm_self_approval?: boolean;
}

export function useSetTimesheetDayRateChoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: SetTimesheetDayRateChoicePayload): Promise<Timesheet> => {
      const response = await apiClient.post<Timesheet>(
        `${PAYROLL_API}/timesheets/${id}/day-rate-choice/`,
        payload
      );
      return response.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.all });
      qc.invalidateQueries({ queryKey: queryKeys.timesheets.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.jobCost.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Job Cost Hooks
// ---------------------------------------------------------------------------

export interface JobCostFilters {
  status?: string;
}

export function useJobCostList(filters: JobCostFilters = {}) {
  return useQuery({
    queryKey: queryKeys.jobCost.list(cleanFilters(filters)),
    queryFn: async (): Promise<JobCostSummary[]> => {
      const response = await apiClient.get<JobCostSummary[]>(
        `${PAYROLL_API}/job-cost/`,
        { params: cleanFilters(filters) }
      );
      return response.data;
    },
  });
}

export function useJobCostDetail(projectId: number) {
  return useQuery({
    queryKey: queryKeys.jobCost.detail(projectId),
    queryFn: async (): Promise<JobCostDetail> => {
      const response = await apiClient.get<JobCostDetail>(
        `${PAYROLL_API}/job-cost/${projectId}/`
      );
      return response.data;
    },
    enabled: !!projectId,
  });
}

// ---------------------------------------------------------------------------
// Pay Period Hooks
// ---------------------------------------------------------------------------

export interface PayPeriodFilters {
  status?: string;
}

export function usePayPeriods(filters: PayPeriodFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payPeriods.list(clean),
    queryFn: async (): Promise<PayPeriod[]> => {
      const response = await apiClient.get<PaginatedResponse<PayPeriod>>(
        `${PAYROLL_API}/pay-periods/`,
        { params: clean }
      );
      return response.data.results;
    },
    enabled,
  });
}

export function usePayPeriod(id: number) {
  return useQuery({
    queryKey: queryKeys.payPeriods.detail(id),
    queryFn: async (): Promise<PayPeriod> => {
      const response = await apiClient.get<PayPeriod>(
        `${PAYROLL_API}/pay-periods/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreatePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePayPeriodPayload): Promise<PayPeriod> => {
      const response = await apiClient.post<PayPeriod>(
        `${PAYROLL_API}/pay-periods/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
    },
  });
}

export function useUpdatePayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { id: number; pay_date: string }): Promise<PayPeriod> => {
      const response = await apiClient.patch<PayPeriod>(
        `${PAYROLL_API}/pay-periods/${data.id}/`,
        { pay_date: data.pay_date }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Payroll Run Hooks
// ---------------------------------------------------------------------------

export interface PayrollRunFilters {
  status?: string;
  pay_period?: number;
}

export function usePayrollRuns(filters: PayrollRunFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payrollRuns.list(clean),
    queryFn: async (): Promise<PaginatedResponse<PayrollRunListItem>> => {
      const response = await apiClient.get<PaginatedResponse<PayrollRunListItem>>(
        `${PAYROLL_API}/runs/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useUnassignedPayrollAdjustments() {
  return useQuery({
    queryKey: [...queryKeys.payrollRuns.all, 'unassigned-adjustments'],
    queryFn: async (): Promise<PayrollAdjustment[]> => {
      const response = await apiClient.get<PayrollAdjustment[]>(
        `${PAYROLL_API}/runs/unassigned-adjustments/`
      );
      return response.data;
    },
  });
}

export function usePayrollRun(id: number) {
  return useQuery({
    queryKey: queryKeys.payrollRuns.detail(id),
    queryFn: async (): Promise<PayrollRun> => {
      const response = await apiClient.get<PayrollRun>(
        `${PAYROLL_API}/runs/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function usePreviewPayroll() {
  return useMutation({
    mutationFn: async (payPeriodId: number): Promise<PayrollPreview> => {
      const response = await apiClient.post<PayrollPreview>(
        `${PAYROLL_API}/runs/preview/`,
        { pay_period_id: payPeriodId }
      );
      return response.data;
    },
  });
}

export function useCalculatePayroll() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payPeriodId: number): Promise<PayrollRunWithReadiness> => {
      const response = await apiClient.post<PayrollRunWithReadiness>(
        `${PAYROLL_API}/runs/calculate/`,
        { pay_period_id: payPeriodId }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.all });
      qc.invalidateQueries({ queryKey: queryKeys.payPeriods.all });
      qc.invalidateQueries({
        queryKey: [...queryKeys.payrollRuns.all, 'unassigned-adjustments'],
      });
    },
  });
}

export function useFinalizePayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<PayrollRun> => {
      const response = await apiClient.post<PayrollRun>(
        `${PAYROLL_API}/runs/${id}/finalize/`
      );
      return response.data;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.all });
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.detail(id) });
    },
  });
}

export function useReopenPayrollRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }): Promise<PayrollRun> => {
      const response = await apiClient.post<PayrollRun>(
        `${PAYROLL_API}/runs/${id}/reopen/`,
        { reason }
      );
      return response.data;
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.all });
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.detail(id) });
    },
  });
}

export function usePayrollRunAdjustments(runId: number) {
  return useQuery({
    queryKey: [...queryKeys.payrollRuns.detail(runId), 'adjustments'],
    queryFn: async (): Promise<PayrollAdjustment[]> => {
      const response = await apiClient.get<PayrollAdjustment[]>(
        `${PAYROLL_API}/runs/${runId}/adjustments/`
      );
      return response.data;
    },
    enabled: runId > 0,
  });
}

export function useCreatePayrollAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      runId: number;
      line_item: number;
      field: PayrollAdjustment['field'];
      new_value: string;
      reason: string;
    }): Promise<PayrollAdjustment> => {
      const { runId, ...payload } = data;
      const response = await apiClient.post<PayrollAdjustment>(
        `${PAYROLL_API}/runs/${runId}/adjustments/`,
        payload
      );
      return response.data;
    },
    onSuccess: (_, { runId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.all });
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.detail(runId) });
      qc.invalidateQueries({ queryKey: [...queryKeys.payrollRuns.detail(runId), 'adjustments'] });
      qc.invalidateQueries({
        queryKey: [...queryKeys.payrollRuns.all, 'unassigned-adjustments'],
      });
    },
  });
}

export function usePayrollRunSnapshots(runId: number) {
  return useQuery({
    queryKey: [...queryKeys.payrollRuns.detail(runId), 'snapshots'],
    queryFn: async (): Promise<PayrollRunSnapshot[]> => {
      const response = await apiClient.get<PayrollRunSnapshot[]>(
        `${PAYROLL_API}/runs/${runId}/snapshots/`
      );
      return response.data;
    },
    enabled: runId > 0,
  });
}

/** @deprecated Use useFinalizePayrollRun. */
export const useApprovePayrollRun = useFinalizePayrollRun;

export interface PayrollExportError extends Error {
  provider?: string;
  missing_employees?: Array<{
    person_id: number;
    user_id: number | null;
    name: string;
    field: string;
  }>;
  adjustment_ids?: number[];
}

export function useExportPayrollRun() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      format = 'csv',
    }: {
      id: number;
      format?: 'csv' | 'quickbooks_iif' | 'xero_csv' | 'gusto_csv' | 'adp_csv';
    }): Promise<Blob> => {
      try {
        const response = await apiClient.get(
          `${PAYROLL_API}/runs/${id}/export/`,
          { params: { export_format: format }, responseType: 'blob' }
        );
        return response.data as Blob;
      } catch (error) {
        const axiosError = error as {
          response?: { status?: number; data?: unknown };
        };
        const response = axiosError.response;
        if (response?.status === 400 && response?.data instanceof Blob) {
          const text = await response.data.text();
          let payload;
          try {
            payload = JSON.parse(text);
          } catch {
            // Not a JSON validation error; rethrow the original error.
            throw error;
          }
          const exportError = new Error(payload.error || 'Export validation failed') as PayrollExportError;
          exportError.provider = payload.provider;
          exportError.missing_employees = payload.missing_employees || [];
          exportError.adjustment_ids = payload.adjustment_ids || [];
          throw exportError;
        }
        throw error;
      }
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.all });
      qc.invalidateQueries({ queryKey: queryKeys.payrollRuns.detail(id) });
    },
  });
}

// ---------------------------------------------------------------------------
// Tech Pay Rate Hooks
// ---------------------------------------------------------------------------

export interface TechPayRateFilters {
  person?: number;
  tech?: number;
  project?: number | 'none';
}

export function useTechPayRates(filters: TechPayRateFilters = {}, enabled = true) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.techPayRates.list(clean),
    queryFn: async (): Promise<TechPayRate[]> => {
      const response = await apiClient.get<TechPayRate[] | { results: TechPayRate[] }>(
        `${PAYROLL_API}/rates/`,
        { params: clean }
      );
      const data = response.data;
      if (Array.isArray(data)) return data;
      if (data && Array.isArray((data as { results: TechPayRate[] }).results)) return (data as { results: TechPayRate[] }).results;
      return [];
    },
    enabled,
  });
}

export function useTechPayRate(id: number) {
  return useQuery({
    queryKey: queryKeys.techPayRates.detail(id),
    queryFn: async (): Promise<TechPayRate> => {
      const response = await apiClient.get<TechPayRate>(
        `${PAYROLL_API}/rates/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateTechPayRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateTechPayRatePayload): Promise<TechPayRate> => {
      const response = await apiClient.post<TechPayRate>(
        `${PAYROLL_API}/rates/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techPayRates.all });
    },
  });
}

export function useUpdateTechPayRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateTechPayRatePayload>;
    }): Promise<TechPayRate> => {
      const response = await apiClient.patch<TechPayRate>(
        `${PAYROLL_API}/rates/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.techPayRates.all });
      qc.invalidateQueries({ queryKey: queryKeys.techPayRates.detail(variables.id) });
    },
  });
}

export function useDeleteTechPayRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/rates/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.techPayRates.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Accounting Export Hooks
// ---------------------------------------------------------------------------

export function useAccountingExports() {
  return useQuery({
    queryKey: queryKeys.accountingExports.list({}),
    queryFn: async (): Promise<AccountingExport[]> => {
      const response = await apiClient.get<PaginatedResponse<AccountingExport>>(
        `${PAYROLL_API}/exports/`
      );
      return response.data.results;
    },
  });
}

// ---------------------------------------------------------------------------
// Cost Code Hooks
// ---------------------------------------------------------------------------

export interface CostCodeFilters {
  is_active?: boolean | '';
  category?: string;
  search?: string;
}

export function useCostCodes(filters: CostCodeFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.costCodes.list(clean),
    queryFn: async (): Promise<CostCode[]> => {
      const response = await apiClient.get<PaginatedResponse<CostCode>>(
        `${PAYROLL_API}/cost-codes/`,
        { params: clean }
      );
      return response.data.results;
    },
  });
}

export function useCostCode(id: number) {
  return useQuery({
    queryKey: queryKeys.costCodes.detail(id),
    queryFn: async (): Promise<CostCode> => {
      const response = await apiClient.get<CostCode>(
        `${PAYROLL_API}/cost-codes/${id}/`
      );
      return response.data;
    },
    enabled: !!id,
  });
}

export function useCreateCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCostCodePayload): Promise<CostCode> => {
      const response = await apiClient.post<CostCode>(
        `${PAYROLL_API}/cost-codes/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.costCodes.all });
    },
  });
}

export function useUpdateCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateCostCodePayload;
    }): Promise<CostCode> => {
      const response = await apiClient.patch<CostCode>(
        `${PAYROLL_API}/cost-codes/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.costCodes.all });
      qc.invalidateQueries({ queryKey: queryKeys.costCodes.detail(variables.id) });
    },
  });
}

export function useDeleteCostCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/cost-codes/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.costCodes.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Reports Hooks
// ---------------------------------------------------------------------------

export interface ExpenseByTechFilters {
  tech_id: number;
  date_from?: string;
  date_to?: string;
  format?: string;
}

export function useExpenseByTechReport(filters: ExpenseByTechFilters) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payrollReports.expenseByTech(clean),
    queryFn: async (): Promise<ExpenseByTechReport> => {
      const response = await apiClient.get<ExpenseByTechReport>(
        `${PAYROLL_API}/reports/expense-by-tech/`,
        { params: clean }
      );
      return response.data;
    },
    enabled: !!filters.tech_id,
  });
}

export interface ExpenseByProjectFilters {
  project_id: number;
  date_from?: string;
  date_to?: string;
  format?: string;
}

export function useExpenseByProjectReport(filters: ExpenseByProjectFilters) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payrollReports.expenseByProject(clean),
    queryFn: async (): Promise<ExpenseByProjectReport> => {
      const response = await apiClient.get<ExpenseByProjectReport>(
        `${PAYROLL_API}/reports/expense-by-project/`,
        { params: clean }
      );
      return response.data;
    },
    enabled: !!filters.project_id,
  });
}

export interface PayrollSummaryFilters {
  pay_period_id: number;
  format?: string;
}

export function usePayrollSummaryReport(filters: PayrollSummaryFilters) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payrollReports.payrollSummary(clean),
    queryFn: async (): Promise<PayrollSummaryReport> => {
      const response = await apiClient.get<PayrollSummaryReport>(
        `${PAYROLL_API}/reports/payroll-summary/`,
        { params: clean }
      );
      return response.data;
    },
    enabled: !!filters.pay_period_id,
  });
}

export interface CostCodeReportFilters {
  date_from?: string;
  date_to?: string;
  format?: string;
}

export function useCostCodeReport(filters: CostCodeReportFilters = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.payrollReports.costCodes(clean),
    queryFn: async (): Promise<CostCodeReport> => {
      const response = await apiClient.get<CostCodeReport>(
        `${PAYROLL_API}/reports/cost-codes/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useDashboardStats(enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.payrollReports.dashboardStats(),
    queryFn: async (): Promise<DashboardStats> => {
      const response = await apiClient.get<DashboardStats>(
        `${PAYROLL_API}/reports/dashboard-stats/`
      );
      return response.data;
    },
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Invoice Pipeline Hooks
// ---------------------------------------------------------------------------

export interface GenerateInvoiceItemsPayload {
  project_id: number;
  date_from: string;
  date_to: string;
  markup_percent?: string;
}

export function useGenerateInvoiceItems() {
  return useMutation({
    mutationFn: async (
      data: GenerateInvoiceItemsPayload
    ): Promise<GenerateInvoiceItemsResponse> => {
      const response = await apiClient.post<GenerateInvoiceItemsResponse>(
        `${PAYROLL_API}/expenses/generate-invoice-items/`,
        data
      );
      return response.data;
    },
  });
}

// ---------------------------------------------------------------------------
// QuickBooks Online Integration Hooks
// ---------------------------------------------------------------------------

export function useQuickBooksStatus() {
  return useQuery({
    queryKey: queryKeys.quickbooks.status(),
    queryFn: async (): Promise<QuickBooksConnectionStatus> => {
      const response = await apiClient.get<QuickBooksConnectionStatus>(
        `${PAYROLL_API}/quickbooks/status/`
      );
      return response.data;
    },
  });
}

export function useQuickBooksConnect() {
  return useMutation({
    mutationFn: async (): Promise<{ authorization_url: string }> => {
      const response = await apiClient.get<{ authorization_url: string }>(
        `${PAYROLL_API}/quickbooks/connect/`
      );
      return response.data;
    },
  });
}

export function useQuickBooksDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      await apiClient.post(`${PAYROLL_API}/quickbooks/disconnect/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}

export function useQuickBooksSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: QuickBooksSyncPayload): Promise<QuickBooksSyncResult> => {
      const response = await apiClient.post<QuickBooksSyncResult>(
        `${PAYROLL_API}/quickbooks/sync/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}

// ---------------------------------------------------------------------------
// QuickBooks Account Mapping Hooks
// ---------------------------------------------------------------------------

export function useQBAccountMappings(filters: Record<string, unknown> = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: [...queryKeys.quickbooks.all, 'mappings', clean],
    queryFn: async (): Promise<QuickBooksAccountMapping[]> => {
      const response = await apiClient.get<PaginatedResponse<QuickBooksAccountMapping>>(
        `${PAYROLL_API}/qb-account-mappings/`,
        { params: clean }
      );
      return response.data.results;
    },
  });
}

export function useCreateQBAccountMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateQBAccountMappingPayload): Promise<QuickBooksAccountMapping> => {
      const response = await apiClient.post<QuickBooksAccountMapping>(
        `${PAYROLL_API}/qb-account-mappings/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}

export function useUpdateQBAccountMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: UpdateQBAccountMappingPayload & { id: number }): Promise<QuickBooksAccountMapping> => {
      const response = await apiClient.patch<QuickBooksAccountMapping>(
        `${PAYROLL_API}/qb-account-mappings/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}

export function useDeleteQBAccountMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/qb-account-mappings/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.quickbooks.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Receipt OCR Hooks
// ---------------------------------------------------------------------------

export function useReceiptOCR() {
  return useMutation({
    mutationFn: async (file: File): Promise<ReceiptOCRResult> => {
      const formData = new FormData();
      formData.append('image', file);
      const response = await apiClient.post<ReceiptOCRResult>(
        `${PAYROLL_API}/receipts/ocr/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Expense Policy Hooks
// ---------------------------------------------------------------------------

export function useExpensePolicies(filters: Record<string, unknown> = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.expensePolicies.list(clean),
    queryFn: async (): Promise<PaginatedResponse<ExpensePolicy>> => {
      const response = await apiClient.get<PaginatedResponse<ExpensePolicy>>(
        `${PAYROLL_API}/policies/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useExpensePolicy(id: number) {
  return useQuery({
    queryKey: queryKeys.expensePolicies.detail(id),
    queryFn: async (): Promise<ExpensePolicy> => {
      const response = await apiClient.get<ExpensePolicy>(
        `${PAYROLL_API}/policies/${id}/`
      );
      return response.data;
    },
    enabled: id > 0,
  });
}

export function useCreateExpensePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateExpensePolicyPayload): Promise<ExpensePolicy> => {
      const response = await apiClient.post<ExpensePolicy>(
        `${PAYROLL_API}/policies/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expensePolicies.all });
    },
  });
}

export function useUpdateExpensePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: CreateExpensePolicyPayload & { id: number }): Promise<ExpensePolicy> => {
      const response = await apiClient.patch<ExpensePolicy>(
        `${PAYROLL_API}/policies/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expensePolicies.all });
    },
  });
}

export function useDeleteExpensePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/policies/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.expensePolicies.all });
    },
  });
}

export function useCheckExpensePolicy() {
  return useMutation({
    mutationFn: async (data: {
      amount: string;
      category_id?: number;
      has_receipt?: boolean;
    }): Promise<PolicyCheckResult> => {
      const response = await apiClient.post<PolicyCheckResult>(
        `${PAYROLL_API}/policies/check/`,
        data
      );
      return response.data;
    },
  });
}

// ---------------------------------------------------------------------------
// Recurring Expense Hooks
// ---------------------------------------------------------------------------

export function useRecurringExpenses(filters: Record<string, unknown> = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.recurringExpenses.list(clean),
    queryFn: async (): Promise<PaginatedResponse<RecurringExpense>> => {
      const response = await apiClient.get<PaginatedResponse<RecurringExpense>>(
        `${PAYROLL_API}/recurring-expenses/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateRecurringExpensePayload): Promise<RecurringExpense> => {
      const response = await apiClient.post<RecurringExpense>(
        `${PAYROLL_API}/recurring-expenses/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringExpenses.all });
    },
  });
}

export function useUpdateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<CreateRecurringExpensePayload> & { id: number }): Promise<RecurringExpense> => {
      const response = await apiClient.patch<RecurringExpense>(
        `${PAYROLL_API}/recurring-expenses/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringExpenses.all });
    },
  });
}

export function useDeleteRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/recurring-expenses/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringExpenses.all });
    },
  });
}

export function useActivateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<RecurringExpense> => {
      const response = await apiClient.post<RecurringExpense>(
        `${PAYROLL_API}/recurring-expenses/${id}/activate/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringExpenses.all });
    },
  });
}

export function useDeactivateRecurringExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<RecurringExpense> => {
      const response = await apiClient.post<RecurringExpense>(
        `${PAYROLL_API}/recurring-expenses/${id}/deactivate/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.recurringExpenses.all });
    },
  });
}

// ─── S17: PM Reconciliation Hooks ───────────────────────────────────────────

export interface ReconciliationFilters {
  project?: number;
  status?: string;
  date_from?: string;
  date_to?: string;
  tech?: number;
}

export function useReconciliationRows(filters: ReconciliationFilters = {}) {
  const params = new URLSearchParams();
  if (filters.project) params.set('project', String(filters.project));
  if (filters.status) params.set('status', filters.status);
  if (filters.date_from) params.set('date_from', filters.date_from);
  if (filters.date_to) params.set('date_to', filters.date_to);
  if (filters.tech) params.set('tech', String(filters.tech));

  const qs = params.toString();

  return useQuery({
    queryKey: ['reconciliation', filters],
    queryFn: async (): Promise<TimesheetReconciliation[]> => {
      const response = await apiClient.get<TimesheetReconciliation[]>(
        `${PAYROLL_API}/reconciliation/${qs ? `?${qs}` : ''}`
      );
      return response.data;
    },
  });
}

export function useGenerateReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      project_id: number;
      date_from: string;
      date_to: string;
    }): Promise<{ created_count: number; rows: TimesheetReconciliation[] }> => {
      const response = await apiClient.post(
        `${PAYROLL_API}/reconciliation/generate/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
    },
  });
}

export function useResolveReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: number;
      action: 'confirm' | 'flag';
      notes?: string;
    }): Promise<TimesheetReconciliation> => {
      const { id, ...body } = payload;
      const response = await apiClient.post<TimesheetReconciliation>(
        `${PAYROLL_API}/reconciliation/${id}/resolve/`,
        body
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
    },
  });
}

export function useBulkResolveReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      ids: number[];
      action: 'confirm' | 'flag';
      notes?: string;
    }): Promise<{ updated_count: number }> => {
      const response = await apiClient.post(
        `${PAYROLL_API}/reconciliation/bulk-resolve/`,
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reconciliation'] });
    },
  });
}

// ---------------------------------------------------------------------------
// S06: Missing Timesheet Detection
// ---------------------------------------------------------------------------

export interface MissingTimesheetTech {
  person_id: number;
  person_name: string;
  tech_id: number | null;
  tech_name: string | null;
  project_id: number | null;
  project_name: string | null;
  assignment_start: string;
  assignment_end: string | null;
}

export interface MissingTimesheetSummary {
  expected_techs: MissingTimesheetTech[];
  submitted_techs: MissingTimesheetTech[];
  missing_techs: MissingTimesheetTech[];
  draft_techs: MissingTimesheetTech[];
  summary: {
    total_expected: number;
    submitted_count: number;
    missing_count: number;
    draft_count: number;
  };
}

export function useMissingTimesheets(payPeriodId: number | undefined) {
  return useQuery({
    queryKey: [...queryKeys.payPeriods.all, 'missing-timesheets', payPeriodId],
    queryFn: async (): Promise<MissingTimesheetSummary> => {
      const response = await apiClient.get<MissingTimesheetSummary>(
        `${PAYROLL_API}/pay-periods/${payPeriodId}/missing-timesheets/`
      );
      return response.data;
    },
    enabled: !!payPeriodId,
  });
}

// ---------------------------------------------------------------------------
// S10: Approval Delegation Hooks
// ---------------------------------------------------------------------------

export function useApprovalDelegations(filters: Record<string, unknown> = {}) {
  const clean = cleanFilters(filters);
  return useQuery({
    queryKey: queryKeys.approvalDelegations.list(clean),
    queryFn: async (): Promise<ApprovalDelegation[]> => {
      const response = await apiClient.get<ApprovalDelegation[]>(
        `${PAYROLL_API}/delegations/`,
        { params: clean }
      );
      return response.data;
    },
  });
}

export function useCreateApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateApprovalDelegationPayload): Promise<ApprovalDelegation> => {
      const response = await apiClient.post<ApprovalDelegation>(
        `${PAYROLL_API}/delegations/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.approvalDelegations.all });
    },
  });
}

export function useUpdateApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...data
    }: Partial<CreateApprovalDelegationPayload> & { id: number }): Promise<ApprovalDelegation> => {
      const response = await apiClient.patch<ApprovalDelegation>(
        `${PAYROLL_API}/delegations/${id}/`,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.approvalDelegations.all });
    },
  });
}

export function useRevokeApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ApprovalDelegation> => {
      const response = await apiClient.post<ApprovalDelegation>(
        `${PAYROLL_API}/delegations/${id}/revoke/`
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.approvalDelegations.all });
    },
  });
}

export function useDeleteApprovalDelegation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`${PAYROLL_API}/delegations/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.approvalDelegations.all });
    },
  });
}
